import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Upload, FileText, CreditCard, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchServiceDetail();
  }, [serviceId]);

  const fetchServiceDetail = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      toast.error('Service not found');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (!user) {
      toast.error('Please login to upload documents');
      navigate('/login');
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const formData = new FormData();
        formData.append('file_name', file.name);
        formData.append('file_data', reader.result);
        formData.append('file_type', file.type);

        await axios.post(`${API}/documents/upload`, formData);
        setUploadedDocs([...uploadedDocs, file.name]);
        toast.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        toast.error('Failed to upload document');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePayment = async () => {
    // Don't require login - allow guest checkout
    const userName = user ? user.name : 'Guest';
    const userEmail = user ? user.email : '';

    setProcessingPayment(true);

    try {
      // Create Razorpay order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        amount: service.price * 100, // Convert to paise
        currency: 'INR',
        receipt: `${service.id}_${Date.now()}`,
        notes: {
          service_name: service.name,
          service_id: service.id,
          user_email: userEmail || 'guest'
        }
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderResponse.data.amount,
        currency: 'INR',
        name: 'Jay Kothari & Company',
        description: service.name,
        order_id: orderResponse.data.id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/payment/verify`, {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature
            });

            // Create service request if user is logged in
            if (user) {
              await axios.post(`${API}/service-requests`, {
                service_type: service.name,
                details: {
                  service_id: service.id,
                  payment_id: response.razorpay_payment_id,
                  amount: service.price,
                  documents: uploadedDocs
                }
              });
              toast.success('Payment successful! Service request created.');
              navigate('/portal');
            } else {
              toast.success('Payment successful! We will contact you shortly.');
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
          contact: user?.phone || ''
        },
        theme: {
          color: '#059669'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setProcessingPayment(false);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Service not found</h2>
          <Link to="/services">
            <Button>Back to Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background py-24 md:py-32" data-testid="service-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/services" className="inline-flex items-center gap-2 text-secondary hover:underline mb-8" data-testid="back-to-services">
          <ArrowLeft className="h-4 w-4" />
          Back to Services
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-secondary mb-2">{service.category}</p>
                    <CardTitle className="text-3xl font-playfair mb-2" data-testid="service-name">{service.name}</CardTitle>
                    <p className="text-4xl font-bold text-secondary" data-testid="service-price">₹{service.price.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Description</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                  {service.pricing_note && (
                    <p className="text-sm text-muted-foreground mt-2 italic">* {service.pricing_note}</p>
                  )}
                </div>

                {service.features && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">What's Included</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-secondary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold mb-3">Required Documents</h3>
                  <ul className="space-y-2">
                    {service.documents.map((doc, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-secondary flex-shrink-0" />
                        <span>{doc}</span>
                      </li>
                    ))}
                  </ul>
                  {service.documents_note && (
                    <p className="mt-4 text-sm text-muted-foreground italic bg-muted/50 p-3 rounded-lg" data-testid="documents-note">
                      {service.documents_note}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Document Upload */}
            <Card data-testid="upload-section">
              <CardHeader>
                <CardTitle className="text-xl">Upload Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-secondary transition-colors cursor-pointer">
                  <Input
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="doc-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    data-testid="document-upload-input"
                  />
                  <Label htmlFor="doc-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-10 w-10 mx-auto mb-3 animate-spin text-secondary" />
                    ) : (
                      <Upload className="h-10 w-10 mx-auto mb-3 text-secondary" />
                    )}
                    <p className="text-sm font-medium mb-1">Click to upload</p>
                    <p className="text-xs text-muted-foreground">PDF, DOC, JPG up to 10MB</p>
                  </Label>
                </div>

                {uploadedDocs.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold">Uploaded Documents:</p>
                    {uploadedDocs.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                        <FileText className="h-4 w-4 text-secondary" />
                        <span className="truncate">{doc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Card */}
            <Card className="border-2 border-secondary/20" data-testid="payment-section">
              <CardHeader>
                <CardTitle className="text-xl">Payment Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service Fee</span>
                    <span className="font-semibold">₹{service.price.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total Amount</span>
                    <span className="text-secondary">₹{service.price.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Razorpay Online Payment */}
                <div>
                  <h3 className="font-semibold mb-3 text-sm">Option 1: Online Payment</h3>
                  <Button
                    onClick={handlePayment}
                    disabled={processingPayment}
                    className="w-full bg-secondary hover:bg-secondary/90"
                    size="lg"
                    data-testid="make-payment-button"
                  >
                    {processingPayment && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay via Razorpay
                  </Button>
                  <div className="text-xs text-center text-muted-foreground mt-2">
                    <p>Supports UPI, Cards, Net Banking</p>
                  </div>
                </div>

                {/* QR Code Payment */}
                {service.qr_code && (
                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3 text-sm">Option 2: Scan QR Code to Pay</h3>
                    <div className="bg-white p-4 rounded-lg border-2 border-dashed border-secondary">
                      <img 
                        src={service.qr_code} 
                        alt="Payment QR Code" 
                        className="w-full max-w-xs mx-auto"
                      />
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-3">
                      Scan this QR code with any UPI app to pay ₹{service.price.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      After payment, WhatsApp payment screenshot to +91 94065 63535
                    </p>
                  </div>
                )}

                {!user && (
                  <div className="bg-muted/50 p-3 rounded text-xs text-center">
                    <Link to="/login" className="text-secondary hover:underline font-semibold">
                      Login to track your service request
                    </Link>
                    <p className="text-muted-foreground mt-1">Or proceed as guest</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3">Need Help?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our team is here to assist you with any questions about this service.
                </p>
                <a href="tel:+919406563535">
                  <Button variant="outline" className="w-full">
                    Call: +91 94065 63535
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
    </div>
  );
};

export default ServiceDetail;
